import { inject } from '@angular/core';
import { StatefulService } from '@app/service/stateful.service';
import { FlowInstance } from '@workflow/class/flow.service';
import { PageInstance } from '@workflow/class/page.service';

/**
 * Helper class to manage co-applicant-related logic.
 * This class is meant to deprecate the old implementation of co-applicant handling.
 */
export class CoApplicantLegacyHelper {
  private readonly statefulService = inject(StatefulService);

  /**
   * Checks if a co-applicant is added.
   * @todo Deprecate this property.
   * @returns `true` if a co-applicant exists, otherwise `false`.
   */
  public get hasCoApplicant(): boolean {
    return (
      (this.statefulService.wrap('api:borrowerInfo/isCoBorrower').value as boolean) ||
      (this.statefulService.wrap('api:oaoMemberInformation/hasCoApplicant').value as boolean) ||
      (this.statefulService.wrap('ola:borrowerInfo/hasCoBorrower').value as boolean)
    );
  }

  /**
   * Checks if an additional co-applicant exists.
   * @returns `true` if an additional co-applicant exists, otherwise `false`.
   */
  public get hasAdditionalCoApplicant(): boolean {
    return this.statefulService.wrap('api:oaoMemberInformation/hasAdditionalCoApplicant').value as boolean;
  }

  /**
   * Filters the list of pages based on the presence of a co-applicant or streamlined flow.
   * @param currentFlow - The current workflow containing the pages.
   * @returns An array of pages that are relevant based on co-applicant status.
   */
  public pagesFilteredByStreamlinedandCoapplicantCheck(currentFlow: FlowInstance): PageInstance[] {
    return currentFlow.pages.filter((page) => {
      let result = !page.config.excludedInFlow && page.visible;
      // For the Streamlined (lockOnPrefill option only available for streamlined flow) flow show applicant pages when its only editable
      // OR page has any reviewable data when locked state.
      if (currentFlow.config.streamlined) {
        result =
          result &&
          (!page.config.lockOnPrefill ||
            (page.config.lockOnPrefill && page.helper.pageVisibleFields.filter((t) => !!t.data?.value?.toString()).length > 0));
      }
      if (!this.hasCoApplicant) {
        result = result && (page.id.includes('-add-co-applicant') || !page.id.includes('-co-applicant'));
      }
      if (!this.hasAdditionalCoApplicant) {
        result = result && page.id.indexOf('-additional-co-app') < 0;
      }
      return result;
    });
  }

  /**
   * Retrieves only reviewable pages from the filtered pages.
   * @param currentFlow - The current workflow containing the pages.
   * @param hideOptionalReviewPages - Whether to hide optional review pages.
   * @returns An array of pages that are reviewable.
   */
  public reviewablePages(currentFlow: FlowInstance, hideOptionalReviewPages: boolean): PageInstance[] {
    return this.pagesFilteredByStreamlinedandCoapplicantCheck(currentFlow).filter(
      (page) => !page.hideInReview && !hideOptionalReviewPages && page.reviewOptional
    );
  }
}
